import React, { useState } from "react";
import Column from "@amzn/meridian/column";
import Row from "@amzn/meridian/row";
import Text from "@amzn/meridian/text";
import Icon from "@amzn/meridian/icon";
import Textarea from "@amzn/meridian/textarea";
import Checkbox from "@amzn/meridian/checkbox";
import Loader from "@amzn/meridian/loader";
import { Sheet } from "@amzn/meridian";
import Button from "@amzn/meridian/button";
import { useNavigate, useLocation } from "react-router-dom";
import thumbsUpTokens from "@amzn/meridian-tokens/base/icon/thumbs-up";
import thumbsDownTokens from "@amzn/meridian-tokens/base/icon/thumbs-down";
import checkCircleTokens from "@amzn/meridian-tokens/base/icon/check-circle-filled";
import PillButton from "src/components/UI/PillButton";
import { submitClassificationFeedback } from "src/services/classificationService";
import { UserReaction, WasteProfilePathway } from "src/types/classificationTypes";

const NEGATIVE_REASONS = [
  "Wrong Recommendations",
  "Wrong SDS document",
  "Conflicts with SDS",
  "Other",
];

interface WasteProfileFeedbackProps {
  asin: string;
  itemName?: string;
  classificationId: string;
  wasteProfileIds: string[];
  siteName: string;
  pathway?: WasteProfilePathway | null;
  isFeedbackCaptured?: boolean;
}

const WasteProfileFeedback: React.FC<WasteProfileFeedbackProps> = ({
  asin,
  itemName,
  classificationId,
  wasteProfileIds,
  siteName,
  pathway,
  isFeedbackCaptured,
}) => {
  const navigate = useNavigate();
  const location = useLocation();
  const [sheetOpen, setSheetOpen] = useState(false);
  const [feedbackType, setFeedbackType] = useState<UserReaction | null>(null);
  const [selectedReasons, setSelectedReasons] = useState<Set<string>>(new Set());
  const [additionalDetails, setAdditionalDetails] = useState("");
  const [submitState, setSubmitState] = useState<"submitting" | "success" | "error" | null>(
    isFeedbackCaptured ? "success" : null,
  );

  function handleThumbsUp() {
    setFeedbackType(UserReaction.LIKE);
    setSheetOpen(true);
  }

  function handleThumbsDown() {
    setFeedbackType(UserReaction.DISLIKE);
    setSheetOpen(true);
  }

  function toggleReason(reason: string) {
    setSelectedReasons((prev) => {
      const next = new Set(prev);
      next.has(reason) ? next.delete(reason) : next.add(reason);
      return next;
    });
  }

  async function handleSubmit() {
    setSheetOpen(false);
    setSubmitState("submitting");
    const userComments = additionalDetails.trim() || undefined;
    const dislikeReasons = selectedReasons.size > 0 ? Array.from(selectedReasons) : undefined;

    try {
      await submitClassificationFeedback({
        asin,
        itemName,
        classificationId,
        wasteProfileIds,
        siteName,
        pathway: pathway ?? WasteProfilePathway.DEFAULT,
        userReaction: feedbackType!,
        dislikeReasons,
        userComments,
      });
      setSubmitState("success");
      navigate(".", { replace: true, state: { ...location.state, isFeedbackCaptured: true } });
    } catch (error) {
      console.error("[WasteProfileFeedback] Failed to submit feedback:", error);
      setSubmitState("error");
    }
  }

  function handleCancel() {
    setSheetOpen(false);
    setSelectedReasons(new Set());
    setAdditionalDetails("");
    setTimeout(() => setFeedbackType(null), 300);
  }

  if (submitState === "submitting") {
    return (
      <div style={{ backgroundColor: "rgba(244, 244, 244, 0.7)" }}>
        <Row spacing="300" alignmentVertical="center" spacingInset="300 400">
          <Loader size="small" type="circular" />
          <Text type="b300" color="secondary">
            Submitting feedback...
          </Text>
        </Row>
      </div>
    );
  }

  if (submitState === "success") {
    return (
      <div style={{ backgroundColor: "#e6f4ea" }}>
        <Row spacing="400" alignmentVertical="center" spacingInset="400">
          <Icon tokens={checkCircleTokens} color="success" />
          <Text type="b300" color="success">
            Thanks! Feedback recorded.
          </Text>
        </Row>
      </div>
    );
  }

  if (submitState === "error") {
    return (
      <div style={{ backgroundColor: "rgba(244, 244, 244, 0.7)" }}>
        <Row alignmentHorizontal="justify" alignmentVertical="center" spacingInset="300 400">
          <Text type="b300" color="error">
            Failed to submit feedback. Please try again.
          </Text>
          <PillButton
            text="RETRY"
            color="outline"
            onClick={() => {
              setSubmitState(null);
              setSheetOpen(true);
            }}
          />
        </Row>
      </div>
    );
  }

  return (
    <>
      <div style={{ backgroundColor: "rgba(244, 244, 244, 0.7)" }}>
        <Row alignmentHorizontal="justify" alignmentVertical="center" spacingInset="300 400">
          <Text type="b300">Is the recommendation(s) correct?</Text>
          <Row spacing="300" alignmentVertical="center">
            <div className="feedback-thumb-border">
              <Button type="icon" circular onClick={handleThumbsUp}>
                <Icon tokens={thumbsUpTokens} />
              </Button>
            </div>
            <div className="feedback-thumb-border">
              <Button type="icon" circular onClick={handleThumbsDown}>
                <Icon tokens={thumbsDownTokens} />
              </Button>
            </div>
          </Row>
        </Row>
      </div>

      <Sheet
        type="overlay"
        side="bottom"
        open={sheetOpen}
        onClose={handleCancel}
        spacingInset="500"
      >
        <Column spacing="400">
          <Column spacing="100">
            <Row spacing="300" alignmentVertical="center" alignmentHorizontal="justify">
              <Text type="b300">
                {feedbackType === UserReaction.LIKE
                  ? "Submit your feedback?"
                  : "What's off about the recommendation(s)?"}
              </Text>
              <Icon
                tokens={feedbackType === UserReaction.LIKE ? thumbsUpTokens : thumbsDownTokens}
                color={feedbackType === UserReaction.LIKE ? "success" : "error"}
              />
            </Row>
            <Text type="b200" color="secondary">
              ASIN: {asin}
            </Text>
          </Column>

          {feedbackType === UserReaction.DISLIKE && (
            <Column spacing="300">
              <Text type="b200" color="secondary">
                Select all that apply
              </Text>
              {NEGATIVE_REASONS.map((reason) => (
                <Checkbox
                  key={reason}
                  checked={selectedReasons.has(reason)}
                  onChange={() => toggleReason(reason)}
                >
                  {reason}
                </Checkbox>
              ))}
            </Column>
          )}

          {feedbackType === UserReaction.DISLIKE && (
            <Textarea
              value={additionalDetails}
              onChange={(value: string) => setAdditionalDetails(value)}
              placeholder="Add more details about what's wrong..."
              rows={3}
            />
          )}

          <Row spacing="300" widths={["fill", "fill"]}>
            <PillButton text="CANCEL" color="outline" onClick={handleCancel} />
            <PillButton
              text="SUBMIT"
              color="success"
              onClick={handleSubmit}
              disabled={
                feedbackType === UserReaction.DISLIKE &&
                (selectedReasons.size === 0 || additionalDetails.trim() === "")
              }
            />
          </Row>
        </Column>
      </Sheet>
    </>
  );
};

export default WasteProfileFeedback;
